import { useEffect, useState } from "react";
import { createExecutionPlan, listExecutionPlans } from "../api/ops.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface PlanStep {
  stepId: string;
  agentName: string;
  description: string;
  status: string;
  dependsOn: string[];
}
interface ExecutionPlan {
  id: string;
  goal: string;
  status: string;
  steps: PlanStep[];
  createdByAgent: string | null;
  createdAt: string;
}

const STEP_COLOR: Record<string, string> = {
  QUEUED: "text-gray-400 border-gray-700",
  RUNNING: "text-blue-400 border-blue-700",
  COMPLETED: "text-green-400 border-green-700",
  FAILED: "text-red-400 border-red-700",
  WAITING: "text-yellow-400 border-yellow-700",
};

export function OpsPlannerPage() {
  const { token } = useAuth();
  const [plans, setPlans] = useState<ExecutionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [goal, setGoal] = useState("");
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<ExecutionPlan | null>(null);

  const reload = () => {
    void listExecutionPlans(token).then((d: unknown) => {
      setPlans((d as { items: ExecutionPlan[] }).items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, []);

  const create = async () => {
    if (!goal.trim()) return;
    setCreating(true);
    const result = await createExecutionPlan(token, { goal });
    setGoal("");
    setCreating(false);
    setSelected(result as ExecutionPlan);
    reload();
  };

  const STATUS_COLOR: Record<string, string> = {
    PENDING: "text-gray-400",
    RUNNING: "text-blue-400",
    COMPLETED: "text-green-400",
    FAILED: "text-red-400",
    CANCELLED: "text-yellow-400",
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <h1 className="mb-6 text-2xl font-bold text-white">AI Planner</h1>
        <div className="mb-6 rounded-lg border border-gray-800 bg-gray-900 p-4">
          <h2 className="mb-3 text-sm font-medium text-gray-300">Create New Plan</h2>
          <div className="flex gap-3">
            <input
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void create()}
              placeholder="Describe your goal... e.g. 'Analyze requirements and create backend API tasks'"
              className="flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
            />
            <button
              onClick={() => void create()}
              disabled={creating || !goal.trim()}
              className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {creating ? "Planning..." : "Create Plan"}
            </button>
          </div>
        </div>
        {selected && (
          <div className="mb-6 rounded-lg border border-indigo-800 bg-gray-900 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-medium text-indigo-300">Plan: {selected.goal}</h2>
              <button
                onClick={() => setSelected(null)}
                className="text-xs text-gray-500 hover:text-gray-300"
              >
                Close
              </button>
            </div>
            <div className="space-y-2">
              {selected.steps.map((step) => (
                <div
                  key={step.stepId}
                  className={`rounded border p-3 text-sm ${STEP_COLOR[step.status] ?? "border-gray-700 text-gray-400"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{step.agentName}</span>
                    <span className="text-xs">{step.status}</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-400">{step.description}</p>
                  {step.dependsOn.length > 0 && (
                    <p className="mt-1 text-xs text-gray-600">
                      Depends on: {step.dependsOn.join(", ")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <h2 className="mb-3 text-lg font-semibold text-white">Execution Plans</h2>
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-2">
            {plans.length === 0 && <p className="text-gray-400">No plans yet.</p>}
            {plans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setSelected(plan)}
                className="w-full rounded-lg border border-gray-800 bg-gray-900 p-3 text-left transition-colors hover:border-gray-600"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white">{plan.goal}</span>
                  <span className={`text-xs ${STATUS_COLOR[plan.status] ?? "text-gray-400"}`}>
                    {plan.status}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {plan.steps.length} steps · {new Date(plan.createdAt).toLocaleString()}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
